import React from "react";
import ReactDOM from "react-dom/client";
import DateFormatter from "../../../formatters/DateFormatter";
import NoResult from "../../../../public/javascripts/reactcomponents/NoResult";
import TicketControlLogic from "../logics/TicketControlLogic";
import enums from "../enums";

/** @typedef {import('../logics/TicketControlLogic').TicketControlItem} TicketControlItem */

/**
 * @typedef {object} TicketControlResult
 * @property {string} status - idle, notfound, validated ou une valeur de TICKET_CONTROL_STATE
 * @property {?number} ticketId - Identifiant du ticket contrôlé
 * @property {string} query - Recherche ayant donné ce résultat
 */

const { VALID, USED, EXPIRED } = enums.TICKET_CONTROL_STATE;
const EMPTY_CONTROL = { status: "idle", ticketId: null, query: "" };
const DEFAULT_FILTERS = { tab: VALID.value, search: "", type: "" };
const STATE_BADGES = { [VALID.value]: "badge--success", [USED.value]: "", [EXPIRED.value]: "badge--danger" };
const STATE_LABELS = { [VALID.value]: VALID.name, [USED.value]: USED.name, [EXPIRED.value]: EXPIRED.name };
const TABS = [
  { value: VALID.value, label: "À utiliser" },
  { value: USED.value, label: "Utilisés" },
  { value: EXPIRED.value, label: "Expirés" },
  { value: "all", label: "Tous" }
];

class Ticket extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      tickets: /** @type {Array<TicketControlItem>} */ ([]),
      durationValidity: 0,
      loading: true,
      saving: false,
      error: "",
      query: "",
      control: /** @type {TicketControlResult} */ (EMPTY_CONTROL),
      filters: DEFAULT_FILTERS,
      isScanReady: false
    };
    this.scanInput = React.createRef();
    // NOTE sur écran tactile, redonner le focus au champ ouvrirait le clavier à chaque contrôle
    this.hasKeyboardScan = window.matchMedia("(pointer: fine)").matches;
  }

  componentDidMount() {
    window.addEventListener("focus", this.updateScanReady);
    window.addEventListener("blur", this.updateScanReady);
    this.focusScan();
    this.loadTickets();
  }

  componentWillUnmount() {
    window.removeEventListener("focus", this.updateScanReady);
    window.removeEventListener("blur", this.updateScanReady);
  }

  /**
   * Charge les tickets vendus et calcule leur expiration avec la durée de validité reçue
   * @returns {Promise<void>}
   */
  async loadTickets() {
    try {
      var response = await fetch("/api/ticket/_search", { headers: { Authorization: "gestiaweb" } });

      if (!response.ok) {
        throw new Error();
      }

      var result = await response.json();

      this.setState({
        tickets: TicketControlLogic.addExpirationInfos(result.tickets, result.durationValidity, new Date()).sort((ticketA, ticketB) => ticketB.id - ticketA.id),
        durationValidity: result.durationValidity,
        loading: false
      });
    } catch (error) {
      this.setState({ loading: false, error: "Impossible de charger les tickets." });
    }
  }

  /**
   * Redonne le focus au champ de scan et sélectionne son contenu pour que le prochain scan le remplace
   */
  focusScan() {
    if (this.hasKeyboardScan) {
      this.scanInput.current.focus();
      this.scanInput.current.select();
    }
    this.updateScanReady();
  }

  /**
   * Une douchette écrit dans le champ actif : si le champ ou la fenêtre perd le focus, le scan n'arrive plus ici
   */
  updateScanReady = () => {
    this.setState({ isScanReady: document.hasFocus() && document.activeElement === this.scanInput.current });
  };

  /**
   * Contrôle un ticket à partir de son identifiant ou de son numéro unique
   * @param {string} query - Valeur scannée ou saisie
   */
  controlTicket(query) {
    var ticket = TicketControlLogic.findTicket(this.state.tickets, query),
      state = ticket && TicketControlLogic.getState(ticket);

    this.setState(
      {
        query,
        error: "",
        control: { status: state || "notfound", ticketId: ticket ? ticket.id : null, query }
      },
      () => this.focusScan()
    );
  }

  /**
   * Lors de la validation du champ, la douchette envoie Entrée après chaque scan : un second Entrée sur le même ticket valide
   * @param {SubmitEvent} event - Événement de soumission du formulaire
   */
  onSubmitScan = event => {
    var query = this.state.query.trim(),
      { control } = this.state;

    event.preventDefault();

    if (!query) {
      return;
    }

    if (query === control.query && control.status === VALID.value) {
      this.setUsed(true);
    } else {
      this.controlTicket(query);
    }
  };

  /**
   * Efface le contrôle en cours avec la touche Échap
   * @param {KeyboardEvent} event - Événement clavier
   */
  onKeyDownScan = event => {
    if (event.key === "Escape") {
      this.setState({ query: "", control: EMPTY_CONTROL });
    }
  };

  /**
   * Passe le ticket contrôlé à utilisé, ou annule son utilisation
   * @param {boolean} isUsed - Nouvelle valeur
   * @returns {Promise<void>}
   */
  setUsed = async isUsed => {
    var { control, saving } = this.state,
      ticket = this.getControlledTicket();

    if (saving || !ticket) {
      return;
    }

    this.setState({ saving: true, error: "" });

    try {
      var response = await fetch("/api/update/ticket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [{ id: ticket.id, is_used: isUsed }], property: "is_used", modelName: "ticket" })
      });

      if (!response.ok) {
        throw new Error();
      }

      var updatedTicket = { ...ticket, is_used: isUsed };

      this.setState(
        {
          saving: false,
          tickets: this.state.tickets.map(item => (item.id === ticket.id ? updatedTicket : item)),
          control: { ...control, status: isUsed ? "validated" : TicketControlLogic.getState(updatedTicket) }
        },
        () => this.focusScan()
      );
    } catch (error) {
      this.setState({ saving: false, error: "Le ticket n'a pas pu être enregistré, réessayez." }, () => this.focusScan());
    }
  };

  /**
   * Ouvre un ticket de la liste dans le verdict
   * @param {TicketControlItem} ticket - Ticket
   */
  openTicket(ticket) {
    this.setState(
      {
        query: ticket.unic_number,
        error: "",
        control: { status: TicketControlLogic.getState(ticket), ticketId: ticket.id, query: ticket.unic_number }
      },
      () => this.focusScan()
    );
  }

  /**
   * Met à jour un filtre de la liste
   * @param {object} filter - Filtre à modifier
   */
  updateFilters(filter) {
    this.setState({ filters: { ...this.state.filters, ...filter } });
  }

  /**
   * Ticket actuellement affiché dans le verdict
   * @returns {?TicketControlItem} Ticket
   */
  getControlledTicket() {
    return this.state.tickets.find(ticket => ticket.id === this.state.control.ticketId) || null;
  }

  /**
   * Lien vers la liste de tous les tickets, y compris ceux qui ne sont pas vendus
   * @param {string} query - Recherche effectuée
   * @returns {string} URL de la liste
   */
  getIndexUrl(query) {
    var property = /^\d+$/.test(query) ? "id" : "unic_number";

    return `/_index/ticket?search[${property}]=${encodeURIComponent(query)}`;
  }

  /**
   * Détail du ticket contrôlé
   * @param {TicketControlItem} ticket - Ticket
   * @returns {any} Composant React
   */
  renderTicketDetails(ticket) {
    return (
      <div className="ticketverdict__body">
        <p className="ticketverdict__number">N°{ticket.unic_number}</p>
        <dl className="ticketverdict__rows">
          <dt>ID</dt>
          <dd>{ticket.id}</dd>
          <dt>Type</dt>
          <dd>{ticket.ticket_type?.name}</dd>
          <dt>Créé le</dt>
          <dd>{DateFormatter.formatDate(ticket.createdAt, true)}</dd>
          <dt>Modifié le</dt>
          <dd>{DateFormatter.formatDate(ticket.updatedAt, true)}</dd>
          <dt>Expire le</dt>
          <dd>{ticket.expirationDate}</dd>
          {ticket.comment && (
            <>
              <dt>Commentaire</dt>
              <dd>{ticket.comment}</dd>
            </>
          )}
        </dl>
      </div>
    );
  }

  /**
   * Boutons d'action du verdict
   * @param {TicketControlItem} ticket - Ticket
   * @returns {any} Composant React
   */
  renderVerdictActions(ticket) {
    var { control, saving } = this.state;

    if (control.status === VALID.value) {
      return (
        <button className="btn btn--success btn--full is-large" type="button" disabled={saving} onClick={() => this.setUsed(true)}>
          <span className="material-icons">task_alt</span>Valider le ticket (Entrée)
        </button>
      );
    }

    if (control.status === EXPIRED.value) {
      return (
        <button className="btn btn--full" type="button" disabled={saving} onClick={() => this.setUsed(true)}>
          <span className="material-icons">task_alt</span>Valider quand même
        </button>
      );
    }

    return (
      <div className="flexline flexline--full flexline--margin">
        <button className="btn" type="button" disabled={saving} onClick={() => this.setUsed(false)}>
          <span className="material-icons">undo</span>
          {control.status === "validated" ? "Annuler la validation" : "Remettre à utiliser"}
        </button>
        {control.status === USED.value && (
          <a className="btn" href={`/_delete/ticket/${ticket.id}?forcereturnurl=/ticket/_selloruse`} data-dialog="true" data-dialog-danger="true" data-dialog-action-title="Supprimer" data-dialog-message={`Supprimer le ticket N°${ticket.unic_number} ? Cette suppression est définitive.`}>
            <span className="material-icons">delete</span>Supprimer
          </a>
        )}
      </div>
    );
  }

  /**
   * Verdict du dernier contrôle
   * @returns {any} Composant React
   */
  renderVerdict() {
    var { control } = this.state,
      ticket = this.getControlledTicket(),
      heads = {
        [VALID.value]: { icon: "check_circle", title: "Ticket valide", text: "Il peut être utilisé" },
        validated: { icon: "verified", title: "Ticket validé", text: "Il est maintenant utilisé" },
        [USED.value]: { icon: "block", title: "Déjà utilisé", text: "Ce ticket ne peut plus servir" },
        [EXPIRED.value]: { icon: "event_busy", title: "Ticket expiré", text: ticket ? `Depuis le ${ticket.expirationDate}` : "" },
        notfound: { icon: "help_outline", title: "Ticket introuvable", text: "Aucun ticket vendu ne correspond" }
      },
      head = heads[control.status];

    if (!head) {
      return (
        <div className="ticketverdict ticketverdict--idle">
          <span className="material-icons">qr_code_2</span>
          <p className="paragraph paragraph--no-margin">En attente d'un scan</p>
        </div>
      );
    }

    return (
      <div className={`ticketverdict ticketverdict--${control.status}`}>
        <div className="ticketverdict__head">
          <span className="material-icons">{head.icon}</span>
          <div>
            <p className="ticketverdict__title">{head.title}</p>
            <p className="ticketverdict__text">{head.text}</p>
          </div>
        </div>
        {ticket ? (
          this.renderTicketDetails(ticket)
        ) : (
          <div className="ticketverdict__body">
            <p className="paragraph paragraph--no-margin">
              Recherche : <strong>{control.query}</strong>. Vérifiez que ce ticket a bien été vendu.
            </p>
          </div>
        )}
        <div className="ticketverdict__stub">
          {ticket ? (
            this.renderVerdictActions(ticket)
          ) : (
            <a className="btn btn--full" href={this.getIndexUrl(control.query)}>
              <span className="material-icons">manage_search</span>Chercher dans tous les tickets
            </a>
          )}
        </div>
      </div>
    );
  }

  /**
   * Liste des tickets filtrés
   * @param {Array<TicketControlItem>} items - Tickets à afficher
   * @returns {any} Composant React
   */
  renderTable(items) {
    var { control } = this.state;

    return (
      <div className="table table--sticky ticketcontrol__table">
        <table className="table__table">
          <thead>
            <tr className="table__tr">
              <th className="table__th table__th-sticky">Numéro</th>
              <th className="table__th table__th-sticky">ID</th>
              <th className="table__th table__th-sticky">Type</th>
              <th className="table__th table__th-sticky">Créé le</th>
              <th className="table__th table__th-sticky">Expire le</th>
              <th className="table__th table__th-sticky">État</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => {
              var state = TicketControlLogic.getState(item);

              return (
                <tr key={item.id} className={`table__tr ticketcontrol__row ${control.ticketId === item.id ? "table__tr--selected" : ""}`} tabIndex={0} onClick={() => this.openTicket(item)} onKeyDown={event => event.key === "Enter" && this.openTicket(item)}>
                  <td className="table__td table__td--align-left">
                    <strong>N°{item.unic_number}</strong>
                  </td>
                  <td className="table__td">{item.id}</td>
                  <td className="table__td table__td--align-left ticketcontrol__type">
                    {item.ticket_type?.name}
                    {item.comment && <p className="paragraph paragraph--no-margin paragraph--small-font-size paragraph--grey-700">{item.comment}</p>}
                  </td>
                  <td className="table__td">{DateFormatter.formatDate(item.createdAt)}</td>
                  <td className="table__td">{item.expirationDate}</td>
                  <td className="table__td">
                    <span className={`badge ${STATE_BADGES[state]}`}>{STATE_LABELS[state]}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    var { tickets, durationValidity, loading, error, query, filters, isScanReady } = this.state,
      counts = TicketControlLogic.countByState(tickets),
      typeNames = TicketControlLogic.getTypeNames(tickets),
      items = TicketControlLogic.filterTickets(tickets, filters);

    return (
      <div className="ticketcontrol">
        <div className="ticketcontrol__desk">
          <form onSubmit={this.onSubmitScan} autoComplete="off">
            <div className="flexline flexline--no-mobile flexline--spaced flexline--align-vz">
              <label className="input-group__label input-group__label--without-margin" htmlFor="ticket-scan">
                Scanner un ticket
              </label>
              {this.hasKeyboardScan && (
                <button className={`badge badge--clickable ${isScanReady ? "badge--success" : "badge--warning"}`} type="button" onClick={() => this.focusScan()}>
                  {isScanReady ? "Prêt à scanner" : "Reprendre le scan"}
                </button>
              )}
            </div>
            <div className="space--8"></div>
            <input
              id="ticket-scan"
              ref={this.scanInput}
              className="input-group__input"
              type="text"
              placeholder="QR code, ID ou numéro unique"
              value={query}
              onChange={event => this.setState({ query: event.target.value })}
              onFocus={event => {
                event.target.select();
                this.updateScanReady();
              }}
              onBlur={this.updateScanReady}
              onKeyDown={this.onKeyDownScan}
            />
          </form>
          {error && <div className="mosaic__badge mosaic__badge--danger">{error}</div>}
          {this.renderVerdict()}
        </div>
        <div className="ticketcontrol__list">
          <div className="buttongroup buttongroup--main-color ticketcontrol__tabs" role="group" aria-label="Filtrer par état">
            {TABS.map(tab => (
              <button key={tab.value} className={`buttongroup__button ${filters.tab === tab.value ? "buttongroup__button--pushed" : ""}`} type="button" aria-pressed={filters.tab === tab.value} onClick={() => this.updateFilters({ tab: tab.value })}>
                {tab.label} <strong>{counts[tab.value]}</strong>
              </button>
            ))}
          </div>
          <div className="ticketcontrol__filters">
            <input className="input-group__input" type="text" placeholder="Numéro, ID ou commentaire" aria-label="Rechercher dans la liste" value={filters.search} onChange={event => this.updateFilters({ search: event.target.value })} />
            <select className="input-group__input" aria-label="Filtrer par type de ticket" value={filters.type} onChange={event => this.updateFilters({ type: event.target.value })}>
              <option value="">Tous les types</option>
              {typeNames.map(typeName => (
                <option key={typeName} value={typeName}>
                  {typeName}
                </option>
              ))}
            </select>
          </div>
          {loading && <p className="paragraph">Chargement des tickets...</p>}
          {!loading && !items.length && <NoResult mode="light" title="Aucun ticket" message="Aucun ticket ne correspond à ces filtres." />}
          {items.length > 0 && this.renderTable(items)}
          {durationValidity > 0 && <p className="paragraph paragraph--no-margin paragraph--small-font-size paragraph--grey-700">Validité des tickets : {durationValidity} mois</p>}
        </div>
      </div>
    );
  }
}

var ticketNode = document.getElementById("ticketcontrol");
if (ticketNode) {
  var root = ReactDOM.createRoot(ticketNode);
  root.render(<Ticket />);
}
